import { Flex, Tag } from '@chakra-ui/react';
import React from 'react';

import type { ButtonItem } from '../../config';
import { useCodingScreen } from '../../interface';
import { readState } from '../../slots/readState';
import { renderPlainText } from '../../slots/renderPlainText';
import { FOOTER_H } from '../../styles/tokens';
import { Slot } from '../Slot';

/** Ascending by `order`, dropping any shortcut marked `visible:false` -- the same ordering/visibility
 *  contract every `Item`-list-driven container in this folder follows, mirroring
 *  `PatientSummaryBar`/`CaseContextBar`'s `visibleFieldsInOrder` for `ButtonItem` instead of
 *  `FieldItem`. Kept local (not shared) so each molecule folder stays self-contained, matching
 *  `ResizableSplit/clampPct.ts`'s precedent. `.filter` on a `readonly ButtonItem[]` already returns a
 *  fresh mutable array, so `.sort` never mutates the config's own (readonly, `as const`) shortcut
 *  list. */
function visibleShortcutsInOrder(
  shortcuts: readonly ButtonItem[]
): ButtonItem[] {
  return shortcuts
    .filter((shortcut) => shortcut.visible)
    .sort((a, b) => a.order - b.order);
}

/**
 * Page footer's LEFT side -- keyboard-shortcut status indicators. `footer.shortcuts` is `[]` in
 * `DEFAULT_CODING_SCREEN_CONFIG` so by default this renders an empty, labelled `role="region"`
 * container with no shortcut children; when a config supplies shortcuts, each visible one renders
 * as a reused Chakra `Tag` showing `shortcut.label` -- this status bar surfaces shortcut labels, it
 * does not wire up `shortcut.contentId` behavior (no business logic; dumb shell).
 *
 * Each shortcut's `Tag` is NON-INTERACTIVE chrome the host always renders (`key`/`data-slot-key`/
 * `data-testid` stay on the `Tag` -- shortcuts are `ButtonItem`s but keep
 * rendering as `Tag`s, not `Button`s); the shortcut's BODY is a `Slot` reading
 * `shortcut.contentId`'s bound value out of injected `state` (`slots/readState.ts`) -- when
 * `shortcut.componentId` resolves in the injected registry the component renders in place of plain
 * text, otherwise the fallback (`renderPlainText(value) ?? shortcut.label`) reproduces exactly what
 * this molecule always rendered: the bound value as text, or `shortcut.label` when there is no
 * bound value (the empty-shell default, `value === undefined` -> `renderPlainText` returns `null`
 * -> the `??` falls through to the label, so the shipped DOM is unchanged).
 */
export const ShortcutStatusBar: React.FC = () => {
  const { config, state } = useCodingScreen();
  const { mode } = config;
  const { shortcutStatus, shortcuts } = config.footer;

  return (
    <Flex
      role="region"
      aria-label="Shortcut status"
      data-content-key={shortcutStatus.contentKey}
      align="center"
      gap={2}
      style={{
        height: FOOTER_H,
        minHeight: FOOTER_H
      }}
    >
      {visibleShortcutsInOrder(shortcuts).map((shortcut) => {
        const value = readState(state, shortcut.contentId);
        return (
          <Tag
            key={shortcut.contentKey}
            data-slot-key={shortcut.contentKey}
            data-testid="shortcut-tag"
          >
            <Slot
              item={shortcut}
              value={value}
              mode={mode}
              fallback={() => renderPlainText(value) ?? shortcut.label}
            />
          </Tag>
        );
      })}
    </Flex>
  );
};
